import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Landmark, Building2, Navigation } from "lucide-react";
import { search as apiSearch } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { Seo } from "@/components/site/Seo";
import { SectionHeading } from "@/components/home/ExploreByState";

const icon = (t) => (["state", "federal_district", "territory"].includes(t) ? Landmark : t === "county" ? Building2 : MapPin);

export default function SearchResults() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [data, setData] = useState({ results: [], matched_zip: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiSearch(q).then((d) => setData(d)).finally(() => setLoading(false));
  }, [q]);

  const results = data.results || [];

  // Human-readable context for disambiguation, e.g. "City in Oregon".
  const contextLabel = (r) =>
    r.state_name && !["state", "federal_district", "territory"].includes(r.geo_type)
      ? `${r.type_label} in ${r.state_name}`
      : r.type_label || (r.geo_type || "").replace("_", " ");

  return (
    <div className="mx-auto max-w-[1100px] px-4 py-12">
      <Seo title={q ? `Search: ${q}` : "Search"} path="/search" noindex description="Search USAPulse for any U.S. state, county, city, town, ZIP code or notable person." />
      <SectionHeading kicker="Search America" title={`Results for "${q}"`} />

      {data.matched_zip && results[0] && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-navy/15 bg-navy/5 px-4 py-3 text-sm text-navy" data-testid="search-zip-banner">
          <Navigation className="h-4 w-4 shrink-0" />
          <span><T>ZIP code</T> <strong>{data.matched_zip}</strong> <T>is in</T> <strong>{results[0].name}</strong>.</span>
        </div>
      )}

      {loading ? (
        <div className="h-40 animate-pulse rounded-xl bg-parchment" />
      ) : results.length === 0 ? (
        <p className="text-muted-foreground"><T>No places found. Try a state, county, city name or a 5-digit ZIP code.</T></p>
      ) : (
        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-white">
          {results.map((r) => {
            const Icon = icon(r.geo_type);
            return (
              <Link key={r.geo_id} to={`/place${r.url_path}`} className="flex items-center gap-4 px-5 py-4 transition-colors hover:bg-parchment" data-testid={`search-page-result-${r.geo_id}`}>
                {r.image_url ? <img src={r.image_url} alt={r.name} className="h-14 w-14 rounded-lg object-cover" /> : <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-navy/5"><Icon className="h-6 w-6 text-navy" /></span>}
                <div className="flex-1">
                  <div className="font-display text-lg font-bold text-navy">{r.name}</div>
                  <div className="text-sm text-muted-foreground" data-testid={`search-context-${r.geo_id}`}>
                    {contextLabel(r)}{r.population ? ` · ${r.population.toLocaleString()}` : ""}
                  </div>
                </div>
                <span className="rounded-full bg-navy/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-navy/70">{r.type_label || r.geo_type.replace("_", " ")}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
